import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feeRuleCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { FeeRuleDto } from "@/features/fee-engine/api/dto";

/**
 * Crée une règle de frais (`POST /FeeRules/add`) — la règle naît en DRAFT
 * (guide §6.5). Son activation (`/activate`) est distincte et passera par
 * maker-checker en phase 8.
 */
export const useCreateFeeRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "fee-rules", "create"],
    mutationFn: (body: FeeRuleDto) =>
      mutateData({
        url: feeEngineEndpoints.feeRules.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<FeeRuleDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, feeRuleCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateFeeRule failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
